import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, HelpCircle, X } from "lucide-react";
import { whatsappUrl } from "./WhatsAppButton";
import FeedbackHelpWidget from "./FeedbackHelpWidget";

// Small WhatsApp glyph (lucide has no brand icon).
function WhatsAppGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

/**
 * Single floating entry point for support. At rest it's one labelled button
 * ("Help"); tapping expands to WhatsApp (priority for our market) and the
 * Help & FAQ panel. Replaces the previously-stacked WhatsApp + Help FABs so the
 * mobile viewport isn't crowded by two buttons.
 */
export default function ContactSpeedDial() {
  const [expanded, setExpanded] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const ref = useRef(null);

  // Collapse the expanded menu on outside click (not when the help panel is open —
  // that has its own dismissal).
  useEffect(() => {
    if (!expanded) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setExpanded(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [expanded]);

  const labelPill =
    "rounded-full bg-card border border-border shadow-md px-3 py-1.5 text-sm font-medium text-foreground whitespace-nowrap";

  // The main FAB closes whichever surface is open; otherwise it expands the menu.
  const showClose = expanded || helpOpen;
  const onMainClick = () => {
    if (helpOpen) { setHelpOpen(false); return; }
    setExpanded((v) => !v);
  };

  return (
    <>
      <div ref={ref} data-speed-dial className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3">
        {/* Expanded actions */}
        {expanded && !helpOpen && (
          <div className="flex flex-col items-end gap-2.5 mb-1">
            {/* WhatsApp — priority channel */}
            <a
              href={whatsappUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setExpanded(false)}
              className="flex items-center gap-2 group"
            >
              <span className={labelPill}>WhatsApp</span>
              <span className="h-11 w-11 rounded-full bg-[#25D366] text-white shadow-lg flex items-center justify-center group-hover:bg-[#1ebe5d] transition-colors">
                <WhatsAppGlyph className="h-5 w-5" />
              </span>
            </a>

            {/* Help & FAQ — opens the controlled panel */}
            <button
              onClick={() => { setHelpOpen(true); setExpanded(false); }}
              className="flex items-center gap-2 group"
            >
              <span className={labelPill}>Help &amp; FAQ</span>
              <span className="h-11 w-11 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center group-hover:bg-primary/90 transition-colors">
                <HelpCircle className="h-5 w-5" />
              </span>
            </button>
          </div>
        )}

        {/* Main labelled FAB */}
        <button
          onClick={onMainClick}
          aria-label={showClose ? "Close" : "Help and contact"}
          aria-expanded={expanded}
          className="flex items-center gap-2 focus:outline-none"
        >
          {!showClose && <span className={`${labelPill} hidden sm:inline-block`}>Help</span>}
          <span className="h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all focus:ring-2 focus:ring-primary focus:ring-offset-2">
            {showClose ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
          </span>
        </button>
      </div>

      <FeedbackHelpWidget open={helpOpen} onOpenChange={setHelpOpen} />
    </>
  );
}
